import { Injectable } from '@angular/core';
import { Storage } from '@ionic/storage';
import structuredClone from '@ungap/structured-clone';
import { Subject } from 'rxjs';
import { throttleTime } from 'rxjs/operators';
import { AlcmeonContextService } from 'src/app/core/alcmeon-context/alcmeon-context.service';
import { SelfV2 } from 'src/app/core/alcmeon-context/models/self-v2.model';
import { SitesService } from 'src/app/mobile/shared/sites.service';
import { SiteV2 } from 'src/app/shared/models';

import { FiltersService } from '../filters/filters.service';
import { QueueIdEnum, TaskListModeEnum } from '../shared/enums';
import { TaskListConfiguration } from '../shared/models';
import { SortService } from '../task-list-sort/task-list-sort.service';
import { NavigationConfigurationService } from './navigation-configuration.service';

/**
 * Migrated from inbox-nav.services.js
 */
@Injectable({
  providedIn: 'root',
})
export class NavigationService {
  private tabsTaskListConfigs: TaskListConfiguration[] = null;
  private channelsTaskListConfigs: TaskListConfiguration[] = null;
  private byMeCounterConfigs: TaskListConfiguration[] = null;
  private selectedSiteIds: number[] = null; // empty array means all allowed sites are requested
  private selectedSiteGroupIds: number[] = null;

  /**
   * Allows to wait 300ms (arbitrary delay) between each click on the box queue.
   */
  loadQueueBS = new Subject().pipe(throttleTime(300)) as Subject<string>;
  loadChannelQueueBS = new Subject().pipe(throttleTime(300)) as Subject<string>;

  constructor(
    private alcmeonContextService: AlcmeonContextService,
    private filtersService: FiltersService,
    private navigationConfigurationService: NavigationConfigurationService,
    private sortService: SortService,
    private storage: Storage,
    private sitesService: SitesService
  ) {}

  private getSiteIdsLocalStorageKey(companyId: number): string {
    return `inbox-nav-selected-site-ids-${companyId}`;
  }

  private getSiteGroupIdsLocalStorageKey(companyId: number): string {
    return `inbox-nav-selected-site-group-ids-${companyId}`;
  }

  // Note that there must be a logged user when calling this method
  async initSelectedSitesAndAvailableTaskListConfigs() {
    const companyId = this.alcmeonContextService.companyId;
    let initialSiteIds = await this.storage.get(this.getSiteIdsLocalStorageKey(companyId));
    let initialSiteGroupIds = await this.storage.get(this.getSiteGroupIdsLocalStorageKey(companyId));

    if (!initialSiteIds) {
      initialSiteIds = [];
    }
    if (!initialSiteGroupIds) {
      initialSiteGroupIds = [];
    }
    this.selectedSiteIds = initialSiteIds;
    this.selectedSiteGroupIds = initialSiteGroupIds;
    this.updateAvailableTaskListConfigs();
  }

  /**
   * Get configuration for current baseQueueId channelQueueId (in url)
   * Called in states.
   */
  async getCurrentTaskListConfiguration(
    baseQueueId: QueueIdEnum,
    channelQueueId: string
  ): Promise<TaskListConfiguration> {
    const config: TaskListConfiguration = this.getTaskListConfigMerged(baseQueueId, channelQueueId);
    if (config) {
      config.filters = await this.filtersService.getFiltersMergedWithSave();
      config.sort_type_id = await this.sortService.getSortId(TaskListModeEnum.Inbox, baseQueueId);
    }

    return config;
  }

  getAllTabsTaskListConfigs(): TaskListConfiguration[] {
    return structuredClone(this.tabsTaskListConfigs);
  }

  getAllChannelsTaskListConfigs(currentTaskListConfiguration: TaskListConfiguration): TaskListConfiguration[] {
    this.channelsTaskListConfigs.forEach(config => {
      const { admin_users_ids, exclude_admin_users_ids, routingTagsIds, site_group_ids, sites_ids, states } =
        currentTaskListConfiguration.parameters;
      const currentParameters = {
        admin_users_ids,
        exclude_admin_users_ids,
        routingTagsIds,
        site_group_ids,
        sites_ids,
        states,
      };
      // concat channel config with current config (tab + filters), to have relevant counters
      config.parameters = { ...config.parameters, ...currentParameters };
    });

    return structuredClone(this.channelsTaskListConfigs);
  }

  getByMeCounterConfigs(): TaskListConfiguration[] {
    return structuredClone(this.byMeCounterConfigs);
  }

  /**
   * Get the configuration of a tab or sub tab for a given queueId
   * @param queueId
   * @returns TaskListConfiguration | null
   */
  getTabTaskListConfigByQueueId(queueId: string): TaskListConfiguration {
    let selectedTaskListTab: TaskListConfiguration = null;
    this.tabsTaskListConfigs.find(config => {
      if (config.queueId === queueId) {
        selectedTaskListTab = config;
        selectedTaskListTab.parentName = config.name;
        return true;
      } else {
        if (
          config.children?.find(subConfig => {
            if (subConfig.queueId === queueId) {
              selectedTaskListTab = subConfig;
              selectedTaskListTab.parentName = config.name;
              return true;
            }
          })
        ) {
          return true;
        }
      }
    });

    return structuredClone(selectedTaskListTab);
  }

  /**
   * Get the configuration of channel for a given queueId
   * @param queueId
   * @returns TaskListConfiguration | null
   */
  getChannelTaskListConfigByQueueId(queueId: string): TaskListConfiguration {
    let selectedTaskListChannel: TaskListConfiguration = null;
    this.channelsTaskListConfigs.find(config => {
      if (config.queueId === queueId) {
        selectedTaskListChannel = config;
        return true;
      }
    });

    return structuredClone(selectedTaskListChannel);
  }

  setSelectedSiteIdsAndUpdateAvailableTaskListConfigs(selectedSiteIds: number[], selectedSiteGroupIds: number[]) {
    this.selectedSiteIds = selectedSiteIds;
    this.selectedSiteGroupIds = selectedSiteGroupIds || [];
    this.updateAvailableTaskListConfigs();
    this.storeSelectedSiteIds(selectedSiteIds, selectedSiteGroupIds);
  }

  private storeSelectedSiteIds(siteIds: number[], siteGroupIds: number[]) {
    const companyId = this.alcmeonContextService.self.companies[0].id;
    this.storage.set(this.getSiteIdsLocalStorageKey(companyId), siteIds);
    this.storage.set(this.getSiteGroupIdsLocalStorageKey(companyId), siteGroupIds);
  }

  /**
   * Builds this.taskListConfigs according to the sites selected in the nav's dropdown (as found in this.selectedSiteIds)
   * An empty array means all sites should be inclued in the inboxes
   */
  private updateAvailableTaskListConfigs() {
    const allAllowedSites: SiteV2[] = this.alcmeonContextService.getAllowedSites(this.sitesService.sitesV2Map);
    const sitesForConfigs: SiteV2[] =
      !this.selectedSiteIds.length && !this.selectedSiteGroupIds.length
        ? allAllowedSites
        : allAllowedSites.filter(
            site => this.selectedSiteIds.includes(site.id) || this.selectedSiteGroupIds.includes(site.site_group_id)
          );

    const selectedSiteGroupIdsSet = new Set(this.selectedSiteGroupIds);
    const siteIdsForConfigs: number[] = sitesForConfigs
      .filter(site => {
        // Separate in groups the sites with group id. Do not request as it.
        const hasSiteSelected = this.selectedSiteIds.includes(site.id);
        if (hasSiteSelected && site.site_group_id) {
          selectedSiteGroupIdsSet.add(site.site_group_id);
          return false;
        }
        return true;
      })
      .map(site => site.id);

    const loggedUser: SelfV2 = this.alcmeonContextService.self;
    const routingTagsIds: number[] = loggedUser.routing_tags.map(el => el.id);
    this.selectedSiteGroupIds = Array.from(selectedSiteGroupIdsSet);

    this.tabsTaskListConfigs = this.navigationConfigurationService.getTabsConfiguration(
      siteIdsForConfigs,
      routingTagsIds,
      this.selectedSiteGroupIds,
      loggedUser
    );

    this.channelsTaskListConfigs = this.navigationConfigurationService.getChannelsConfiguration(sitesForConfigs);

    this.byMeCounterConfigs = this.navigationConfigurationService.getByMeConfiguration(
      siteIdsForConfigs,
      routingTagsIds,
      this.selectedSiteGroupIds,
      loggedUser
    );
  }

  /**
   * Get a dynamic config of a tab + channel
   *
   * @param baseQueueId queueId of a tab, or a channel in some cases (when force redirected from old inbox for ex)
   * @param channelQueueId queueId of a channel
   */
  private getTaskListConfigMerged(baseQueueId: string, channelQueueId: string): TaskListConfiguration {
    let parentName = '';
    let tabSelectedTaskConfig = this.getTabTaskListConfigByQueueId(baseQueueId);

    if (!tabSelectedTaskConfig) {
      const channelSelectedTaskConfig = this.getChannelTaskListConfigByQueueId(baseQueueId);

      // a channel match baseQueueId, so we want default queueId
      if (channelSelectedTaskConfig) {
        channelQueueId = baseQueueId;
        tabSelectedTaskConfig = this.getTabTaskListConfigByQueueId(QueueIdEnum.Default);
        tabSelectedTaskConfig = this.mergeTabWithChannelConfig(tabSelectedTaskConfig, channelSelectedTaskConfig);
      } else {
        return null;
      }
    } else {
      parentName = tabSelectedTaskConfig.parentName;

      if (channelQueueId !== 'all') {
        const channelSelectedTaskConfig = this.getChannelTaskListConfigByQueueId(channelQueueId);

        if (channelSelectedTaskConfig) {
          tabSelectedTaskConfig = this.mergeTabWithChannelConfig(tabSelectedTaskConfig, channelSelectedTaskConfig);
        }
      }
    }

    const { queueId, title, parameters } = tabSelectedTaskConfig;

    return {
      channelQueueId,
      parentName,
      parameters,
      queueId,
      title,
    };
  }

  /**
   * Merge a config of a tab with the config of a channel.
   * The goal is to filter the tab with one channel.
   */
  private mergeTabWithChannelConfig(
    currentConfiguration: TaskListConfiguration,
    channelConfiguration: TaskListConfiguration
  ): TaskListConfiguration {
    const title = `${currentConfiguration.title} › ${channelConfiguration.title}`;

    const { type, wantsPublicationFilter, wantsMentionFilter, wantsPublicRepliesFilter } =
      channelConfiguration.parameters;

    currentConfiguration.title = title;
    currentConfiguration.parameters.type = type;
    currentConfiguration.parameters.wantsPublicationFilter = wantsPublicationFilter;
    currentConfiguration.parameters.wantsMentionFilter = wantsMentionFilter;
    currentConfiguration.parameters.wantsPublicRepliesFilter = wantsPublicRepliesFilter;

    return currentConfiguration;
  }

  getSelectedSiteIds() {
    return {
      siteIds: this.selectedSiteIds || [],
      siteGroupIds: this.selectedSiteGroupIds || [],
    };
  }
}
